import React from "react";
import { Icon } from "@dialogueconsulting/sked-ui";

import { cn } from "../../../../../lib/utils";
import { Button } from "../../../../ui/button";
import { Spinner } from "../../../../ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../ui/tooltip";

export const AudioIconAction: React.FC<{
  tooltip: string;
  children: React.ReactElement;
}> = ({ tooltip, children }) => (
  <Tooltip>
    <TooltipTrigger asChild>{children}</TooltipTrigger>
    <TooltipContent side="top">{tooltip}</TooltipContent>
  </Tooltip>
);

interface AudioPreviewButtonProps {
  title: string;
  playing: boolean;
  buffering?: boolean;
  disabled?: boolean;
  onToggle(): void;
}

export const AudioPreviewButton: React.FC<AudioPreviewButtonProps> = ({
  title,
  playing,
  buffering = false,
  disabled,
  onToggle,
}) => {
  const label = buffering
    ? `Loading preview of ${title}`
    : playing
      ? `Stop preview of ${title}`
      : `Play preview of ${title}`;

  return (
    <AudioIconAction
      tooltip={
        playing
          ? "Stop preview"
          : "Play the track on its own. Instagram cannot preview it with your reel."
      }
    >
      <Button
        type="button"
        variant="black-secondary"
        size="icon-xs"
        aria-label={label}
        aria-pressed={playing}
        aria-busy={buffering || undefined}
        disabled={disabled}
        className={cn(
          "relative z-10",
          playing && "border-blue-500 text-blue-500"
        )}
        onClick={onToggle}
      >
        {buffering ? (
          <Spinner
            size="sm"
            aria-hidden="true"
            className="motion-reduce:animate-none"
          />
        ) : (
          <i
            className={cn("fa-solid text-xs", playing ? "fa-stop" : "fa-play")}
            aria-hidden="true"
          />
        )}
      </Button>
    </AudioIconAction>
  );
};

export const HearOnInstagramLink: React.FC<{ title: string; href: string }> = ({
  title,
  href,
}) => (
  <AudioIconAction tooltip="Hear on Instagram">
    <Button
      asChild
      variant="black-secondary"
      size="icon-xs"
      className="relative z-10"
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Hear ${title} on Instagram (opens in a new tab)`}
      >
        <Icon name="link-external-01" size="xs" aria-hidden="true" />
      </a>
    </Button>
  </AudioIconAction>
);

export const AudioRemoveButton: React.FC<{
  title: string;
  disabled?: boolean;
  onRemove(): void;
}> = ({ title, disabled, onRemove }) => (
  <AudioIconAction tooltip="Remove track">
    <Button
      type="button"
      variant="black-tertiary"
      size="icon-sm"
      aria-label={`Remove ${title}`}
      disabled={disabled}
      onClick={onRemove}
    >
      <i className="fa-regular fa-xmark text-sm" aria-hidden="true" />
    </Button>
  </AudioIconAction>
);
